import type { Drill } from "./model/drill";

/** 新規スキルの習得期に連続して解かせる問数（blocked practice）。 */
export const ACQUISITION_BLOCK_SIZE = 2;

export type PracticePhase = "acquisition" | "retrieval";

export interface ScheduledDrill {
  readonly drill: Drill;
  readonly phase: PracticePhase;
}

/**
 * 演習のスキル鍵。`requiredCommands` を結合した文字列（例: `l` / `dw`）。
 * 無い演習はスケジューラが「スキル無し」として末尾へ回す。
 */
export function skillKeyOf(drill: Drill): string | null {
  const commands = drill.goal.requiredCommands;
  if (commands === undefined || commands.length === 0) {
    return null;
  }
  return commands.join("");
}

/**
 * 記憶定着のための出題順を組む。
 *
 * 根拠（運動学習の文脈干渉・望ましい困難）:
 * - 新しい技は最初の数問だけブロックし、型を掴ませる（acquisition）
 * - 以降は導入済みの技と混ぜて想起させる（retrieval / interleaving）
 * - 同技の連続だけ（massed）だと習得中の手応えは良いが、遅延保持が弱くなる
 *
 * アルゴリズム:
 * 1. 出現順でスキルをグループ化
 * 2. 各スキルの先頭 {@link ACQUISITION_BLOCK_SIZE} 問を、スキル順にブロック配置
 * 3. 残りの問を、導入済みスキル間でラウンドロビンにインターリーブ
 * 4. スキル鍵のない演習は最後に出現順のまま付ける
 */
export function scheduleDrillsForRetention(drills: readonly Drill[]): readonly ScheduledDrill[] {
  const groups = new Map<string, Drill[]>();
  const skillOrder: string[] = [];
  const unkeyed: Drill[] = [];

  for (const drill of drills) {
    const key = skillKeyOf(drill);
    if (key === null) {
      unkeyed.push(drill);
      continue;
    }
    const bucket = groups.get(key);
    if (bucket === undefined) {
      groups.set(key, [drill]);
      skillOrder.push(key);
    } else {
      bucket.push(drill);
    }
  }

  const scheduled: ScheduledDrill[] = [];
  const remainders = new Map<string, Drill[]>();
  const introduced: string[] = [];

  const pullOneRetrieval = (skillKey: string): void => {
    const queue = remainders.get(skillKey);
    if (queue === undefined || queue.length === 0) {
      return;
    }
    const next = queue.shift();
    if (next !== undefined) {
      scheduled.push({ drill: next, phase: "retrieval" });
    }
    if (queue.length === 0) {
      remainders.delete(skillKey);
    }
  };

  for (const key of skillOrder) {
    const bucket = groups.get(key) ?? [];
    const acquisition = bucket.slice(0, ACQUISITION_BLOCK_SIZE);
    const rest = bucket.slice(ACQUISITION_BLOCK_SIZE);
    for (const drill of acquisition) {
      scheduled.push({ drill, phase: "acquisition" });
    }
    if (rest.length > 0) {
      remainders.set(key, [...rest]);
    }
    introduced.push(key);

    // 2つ目以降の技を入れた直後に想起を挟む。
    // 直前に習得した技そのものは連続を避けるため後回しにし、先に他スキルを想起させる。
    if (introduced.length < 2) {
      continue;
    }
    for (const prior of introduced) {
      if (prior === key) {
        continue;
      }
      pullOneRetrieval(prior);
    }
    pullOneRetrieval(key);
  }

  // 残った問を導入順のラウンドロビンで吐き切る。
  const activeKeys = skillOrder.filter((key) => (remainders.get(key)?.length ?? 0) > 0);
  while (activeKeys.length > 0) {
    for (let i = 0; i < activeKeys.length; ) {
      const key = activeKeys[i] as string;
      const queue = remainders.get(key) ?? [];
      const next = queue.shift();
      if (next !== undefined) {
        scheduled.push({ drill: next, phase: "retrieval" });
      }
      if (queue.length === 0) {
        remainders.delete(key);
        activeKeys.splice(i, 1);
      } else {
        i += 1;
      }
    }
  }

  for (const drill of unkeyed) {
    scheduled.push({ drill, phase: "retrieval" });
  }

  return scheduled;
}

/**
 * 想起練習用に、課題文の先頭からキー名の手がかりを落とす。
 * 例: `l を3回押して、カーソルを3文字右へ動かそう。` → `カーソルを3文字右へ動かそう。`
 */
export function toRetrievalPrompt(
  instruction: string,
  requiredCommands: readonly string[] = [],
): string {
  let text = instruction.trim();
  const candidates = [...requiredCommands]
    .map((command) => command.trim())
    .filter((command) => command.length > 0)
    .sort((a, b) => b.length - a.length);

  for (const command of candidates) {
    const escaped = command.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const patterns = [
      new RegExp(`^[\`']?${escaped}[\`']?\\s*を\\s*\\d*\\s*回?押して[、,]?\\s*`),
      new RegExp(`^[\`']?${escaped}[\`']?\\s*で\\s*`),
      new RegExp(`^[\`']?${escaped}[\`']?\\s*を\\s*`),
      new RegExp(`^[\`']?${escaped}[\`']?\\s*`),
    ];
    for (const pattern of patterns) {
      if (pattern.test(text)) {
        text = text.replace(pattern, "");
        break;
      }
    }
  }

  text = text.replace(/^[、,。\s]+/, "").trim();
  if (text.length === 0) {
    return "的を満たす技を思い出して打ち込もう。";
  }
  return text;
}

/** 出題フェーズに応じて学習者へ見せる Drill を返す（acquisition は原文のまま）。 */
export function presentDrillForPhase(drill: Drill, phase: PracticePhase): Drill {
  if (phase === "acquisition") {
    return drill;
  }
  return {
    ...drill,
    instruction: toRetrievalPrompt(drill.instruction, drill.goal.requiredCommands ?? []),
  };
}
